import * as React from "react";

/**
 * Options configuring a {@link usePaginatedFilters} state machine.
 */
interface UsePaginatedFiltersOptions<TDraft, TActive> {
  /** Initial (and "cleared") value of the draft/form state. */
  emptyDraft: TDraft;
  /** Initial (and "cleared") value of the committed filters passed to the API. */
  emptyActive: TActive;
  /** Convert draft form state into the committed filter shape. Called by {@link search}. */
  toActive: (draft: TDraft) => TActive;
}

/**
 * State and actions returned by {@link usePaginatedFilters}.
 */
export interface UsePaginatedFiltersResult<TDraft, TActive> {
  draft: TDraft;
  setDraft: React.Dispatch<React.SetStateAction<TDraft>>;

  /** Committed filters; the API query should react to changes in this object. */
  active: TActive;
  setActive: React.Dispatch<React.SetStateAction<TActive>>;

  pageToken: string | undefined;
  currentPage: number;
  hasPreviousPage: boolean;

  /** Commit {@link draft} into {@link active} and reset to page 0. */
  search: () => void;
  /** Replace {@link draft} with ``nextDraft``, then commit and reset.
   *
   * Use this when an outside event (e.g. "show feedback for THIS thread" from
   * the threads table) needs to set the draft and commit it in one render —
   * ``setDraft`` followed by ``search`` would commit the closed-over old
   * draft, since React batches the state update. */
  searchWith: (nextDraft: TDraft) => void;
  /** Reset draft, active, and pagination to their initial values. */
  clear: () => void;
  /** Reset pagination only; leaves draft and active untouched. */
  resetPagination: () => void;
  /** Advance to the next page using the server-provided next_token. */
  nextPage: (nextToken: string) => void;
  /** Step back one page using the accumulated token history. */
  previousPage: () => void;
}

/**
 * Paginated, filterable listing state with a draft/active split.
 *
 * The "draft" is what the filter UI binds to; the "active" snapshot is what
 * the query hook reads. They diverge until the user presses Search, which
 * calls ``toActive(draft)`` and resets pagination. Two views sharing the
 * same pagination + filter idiom can each own an instance without
 * reimplementing the bookkeeping.
 *
 * ``setActive`` is exposed for the cases where active filters change from
 * outside the form — deep-link hydration and cross-view navigation.
 */
// ``null`` (rather than ``""``) is the "first page" sentinel inside the
// history stack. The empty string can be a valid cursor on some servers, so
// using ``""`` for "no cursor" would collide if the API ever returned one.
type PageTokenHistory = ReadonlyArray<string | null>;

export function usePaginatedFilters<TDraft, TActive>({
  emptyDraft,
  emptyActive,
  toActive,
}: UsePaginatedFiltersOptions<TDraft, TActive>): UsePaginatedFiltersResult<
  TDraft,
  TActive
> {
  const [draft, setDraft] = React.useState<TDraft>(emptyDraft);
  const [active, setActive] = React.useState<TActive>(emptyActive);
  const [pageToken, setPageToken] = React.useState<string | undefined>();
  const [pageTokenHistory, setPageTokenHistory] =
    React.useState<PageTokenHistory>([]);

  // ``currentPage`` and ``hasPreviousPage`` are derived from
  // ``pageTokenHistory.length`` rather than tracked in a parallel state slot.
  // The previous design kept ``currentPage`` as its own state, which let it
  // desync from the history if anything mutated only one of them.
  const currentPage = pageTokenHistory.length;
  const hasPreviousPage = currentPage > 0;

  const resetPagination = React.useCallback(() => {
    setPageToken(undefined);
    setPageTokenHistory([]);
  }, []);

  const search = React.useCallback(() => {
    setActive(toActive(draft));
    setPageToken(undefined);
    setPageTokenHistory([]);
  }, [draft, toActive]);

  const searchWith = React.useCallback(
    (nextDraft: TDraft) => {
      setDraft(nextDraft);
      setActive(toActive(nextDraft));
      setPageToken(undefined);
      setPageTokenHistory([]);
    },
    [toActive],
  );

  const clear = React.useCallback(() => {
    setDraft(emptyDraft);
    setActive(emptyActive);
    setPageToken(undefined);
    setPageTokenHistory([]);
  }, [emptyDraft, emptyActive]);

  const nextPage = React.useCallback(
    (nextToken: string) => {
      // Push the *current* token (or ``null`` for the first page) onto
      // history, then advance.
      setPageTokenHistory((prev) => [...prev, pageToken ?? null]);
      setPageToken(nextToken);
    },
    [pageToken],
  );

  const previousPage = React.useCallback(() => {
    // Don't reach into another setter from within an updater (the original
    // form invoked ``setPageToken`` from inside the ``setPageTokenHistory``
    // callback, breaking the "updaters are pure" rule). Read the history
    // once via the functional setter, then issue the page-token update
    // separately.
    setPageTokenHistory((prev) => {
      if (prev.length === 0) {
        return prev;
      }
      const previousToken = prev[prev.length - 1];
      // Convert the ``null`` sentinel back to ``undefined`` for the API
      // ("no cursor"); leave a real string (including the empty string,
      // which a server might legitimately use as the first cursor) alone.
      setPageToken(previousToken === null ? undefined : previousToken);
      return prev.slice(0, -1);
    });
  }, []);

  return {
    draft,
    setDraft,
    active,
    setActive,
    pageToken,
    currentPage,
    hasPreviousPage,
    search,
    searchWith,
    clear,
    resetPagination,
    nextPage,
    previousPage,
  };
}
